import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AmountCard, Card, EmptyState, ExplainerBanner, Icon, Sheet, Skeleton, StatusChip, Textarea } from '@/ds'
import { NotAvailable } from '@/app/components/NotAvailable'
import { formatMoney } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'
import { pagosService } from '@/services/pagos'
import { useSave } from '../Checkin/useSave'
import { SectionError } from '../Estancia/sections/SectionError'
import { BackLink, PAY_ROUTES, stayName, usePagosStay } from './shared'

/**
 * 8.3 · Tasa turística (Pagos.dc.html screen=tax, Bloque 3 §3f). No existe en la API actual: todo contra mock;
 * en real se muestra el estado vacío (D36). Quién está exento depende del municipio y lo da el backend.
 */
export function Tasa() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const stay = usePagosStay()
  const [tax, reload] = useAsync(`tasa:${stay.guestCode}`, (signal) => pagosService.getTouristTax(stay, signal))
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [missing, setMissing] = useState(false)
  const [sent, setSent] = useState(false)
  const { saving, error, run } = useSave()
  const value = tax.kind === 'ready' ? tax.value : null

  const send = async () => {
    if (!text.trim()) return setMissing(true)
    const ok = await run(() => pagosService.notifyTaxExemption(stay, text.trim()))
    if (ok) {
      setOpen(false)
      setSent(true)
    }
  }

  return (
    <main className="page pagos">
      <div className="pagos__head">
        <BackLink to={PAY_ROUTES.payments} label={t('v2.pagos.pendiente.titulo')} />
        <h1>{t('v2.pagos.tasa.titulo')}</h1>
        {value && <p className="page__lead">{t('v2.pagos.tasa.lead', { name: stayName(stay), persons: t('v2.pagos.tasa.personas', { count: value.persons }), nights: t('v2.pagos.tasa.noches', { count: value.nights }) })}</p>}
      </div>
      {tax.kind === 'loading' && (
        <div aria-busy="true" className="pagos__stack">
          <Skeleton height={200} />
          <Skeleton height={240} />
        </div>
      )}
      {tax.kind === 'error' && <SectionError code={tax.code} onRetry={reload} />}
      {tax.kind === 'unsupported' && <NotAvailable consequences={[t('v2.noDisponible.consecuencia.tasa')]} />}
      {tax.kind === 'ready' && !value && (
        <EmptyState icon="file-text" title={t('v2.pagos.tasa.sinTasa')} headingLevel={2}>
          {t('v2.pagos.tasa.sinTasaTexto')}
        </EmptyState>
      )}
      {value && (
        <>
          <AmountCard total={formatMoney(value.total, lang)} totalLabel={t('v2.pagos.tasa.tuTasa')} buttonLabel={null}>
            <dl className="pagos__rows">
              <div className="pagos__row">
                <dt>{t('v2.pagos.tasa.porPersona', { amount: formatMoney(value.perPersonNight, lang) })}</dt>
                <dd className="tnum">{t('v2.pagos.tasa.calculo', { persons: value.persons, nights: t('v2.pagos.tasa.noches', { count: value.nights }) })}</dd>
              </div>
              <div className="pagos__row">
                <dt>{t('v2.pagos.tasa.exentas')}</dt>
                <dd className="tnum">{value.exemptPersons}</dd>
              </div>
            </dl>
            <p className="pagos__note">{t('v2.pagos.tasa.nota')}</p>
          </AmountCard>
          {/* Término de dinero con el banner explicador del sistema, como la fianza (DESIGN.md §4 y §7, fase 4 A10). */}
          <ExplainerBanner title={t('v2.pagos.pendiente.queTasa')}>{t('v2.pagos.tasa.queEsTexto')}</ExplainerBanner>
          <Card>
            <div className="pagos__stack">
              {value.exemptions.length > 0 && (
                <div className="pagos__group">
                  <h2 className="pagos__h3">{t('v2.pagos.tasa.quien')}</h2>
                  <ul className="pagos__list">
                    {value.exemptions.map((e) => (
                      <li key={e}>{e}</li>
                    ))}
                  </ul>
                </div>
              )}
              {sent ? (
                <StatusChip tone="success">{t('v2.pagos.tasa.enviado')}</StatusChip>
              ) : (
                <button type="button" className="pagos__chevron-link" onClick={() => setOpen(true)}>
                  {t('v2.pagos.tasa.avisar')}
                  <Icon name="chevron-right" size={20} />
                </button>
              )}
            </div>
          </Card>
          <Sheet open={open} title={t('v2.pagos.tasa.hojaTitulo')} confirmLabel={t('v2.pagos.tasa.enviar')} onConfirm={() => void send()} onCancel={() => setOpen(false)} loading={saving}>
            <div className="pagos__group">
              <Textarea
                label={t('v2.pagos.tasa.hojaCampo')}
                value={text}
                onChange={(e) => {
                  setText(e.target.value)
                  setMissing(false)
                }}
                error={missing ? t('v2.legacy.validations.Required') : undefined}
              />
              {error && (
                <p className="pagos__error" role="alert">
                  {error}
                </p>
              )}
            </div>
          </Sheet>
        </>
      )}
    </main>
  )
}
